import { assert, CheckedAddress, Percentage } from '@sparkdotfi/common-universal'
import { useQuery } from '@tanstack/react-query'
import { mainnet } from 'viem/chains'
import { useConfig } from 'wagmi'

import { getChainConfigEntry } from '@/config/chain'
import { useSandbox } from '@/domain/sandbox'
import { useTokenRepositoryQuery } from '@/domain/token-repository/useTokenRepository'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { getFarmContractData } from '@/features/spk/farm/data/getFarmContractData'
import { spkFarmDataQueryKey } from '@/features/spk/farm/data/useSpkFarmData'
import { calculateFarmApy } from '@/features/spk/farm/utils/calculateFarmApy'

export function useSpkFarmApy(): Percentage | undefined {
  const wagmiConfig = useConfig()
  const { account } = useConnectedAddress({ throwOnNotConnected: false })
  const { isInSandbox, sandboxChainId } = useSandbox()

  const chainId = isInSandbox && sandboxChainId ? sandboxChainId : mainnet.id

  const spk = getChainConfigEntry(mainnet.id).spk
  assert(spk, 'Spk feature config not found')
  const { data: tokenRepository } = useTokenRepositoryQuery({
    chainId,
    priceConfigs: spk.priceConfigs,
    account,
  })

  const { data: farmData } = useQuery({
    queryKey: spkFarmDataQueryKey({ chainId, account }),
    queryFn: () =>
      getFarmContractData({
        farmAddress: spk.farmConfig.address,
        tokenRepository: tokenRepository!,
        chainId,
        account: account ?? CheckedAddress.ZERO(),
        wagmiConfig,
      }),
    enabled: !!tokenRepository,
  })

  const apy = farmData && calculateFarmApy(farmData)
  return apy?.gt(0) ? apy : undefined
}
